import React, { useEffect } from "react";
import { Paper, Popover, styled } from "@mui/material";
import { CoreHeader } from "@tanstack/react-table";
import { DefaultRow } from "../../types";
import { CheckboxGroup } from "../../../CheckboxGroup";

const ALL_FILTER_VALUE = "ALL";

type MetaFilterOptions = {
  getFilterLabel: (value: string) => string;
};

export interface FilterOptionsProps<TRow> {
  column: CoreHeader<TRow, unknown>["column"];
  anchorEl?: HTMLElement | null;
  onClose?: () => void;
}
export const MAX_FILTER_OPTIONS = 200;
export const ColumnFilterOptions = <TRow extends DefaultRow>({
  column,
  anchorEl,
  onClose,
}: FilterOptionsProps<TRow>) => {
  const getFilterOptionLabel = (column.columnDef.meta as MetaFilterOptions)
    ?.getFilterLabel;
  const sortedUniqueValues = React.useMemo(() => {
    const uniqueValues = Array.from(column.getFacetedUniqueValues().keys())
      .sort()
      .slice(0, MAX_FILTER_OPTIONS);

    return uniqueValues;
  }, [column.getFacetedUniqueValues()]);

  const selectedFilters = (column.getFilterValue() ?? []) as string[];

  const options = [
    {
      value: ALL_FILTER_VALUE,
      label: getFilterOptionLabel?.(ALL_FILTER_VALUE),
      checked: ((column?.getFilterValue() ?? []) as string[]).length === 0,
    },
    ...sortedUniqueValues.map((value) => ({
      value,
      label: getFilterOptionLabel?.(value),
      checked: selectedFilters.includes(value),
    })),
  ];
  const handleToggle = (
    e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    e.stopPropagation();

    const name = e.target.name;

    if (name === ALL_FILTER_VALUE) {
      if (checked) {
        return column.setFilterValue([]);
      }
      return column.setFilterValue(sortedUniqueValues);
    }

    const newSelected = !checked
      ? selectedFilters?.filter((v) => v !== name)
      : [...selectedFilters, name];

    if (newSelected.length === sortedUniqueValues.length) {
      return column.setFilterValue([]);
    }
    return column.setFilterValue(newSelected);
  };
  return (
    <Popover
      id={"simple-popper"}
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      onClose={onClose}
      onClick={(e) => e.stopPropagation()}
    >
      <StyledOptionsWrapper>
        <CheckboxGroup data={options} onChange={handleToggle} />
      </StyledOptionsWrapper>
    </Popover>
  );
};
const StyledOptionsWrapper = styled(Paper)(({ theme }) => ({
  padding: "1rem",
  backgroundColor: theme.tokens.colors.gray[1],

  borderRadius: 0,
  ".MuiFormGroup-root": {
    gap: "0.5rem",
  },
  ".MuiFormControlLabel-root": {
    margin: 0,
    gap: "0.5rem",
    textTransform: "capitalize",
    lineHeight: "1rem",
    ">span": {
      padding: "0",
    },
    "&.label-id-ALL": {
      borderBottom: `2px solid ${theme.tokens.colors.gray[4]}`,
      paddingBottom: "0.5rem",
    },
  },
}));
